<template>
  <div>
    <div
      v-if="plugin && plugin.id"
      :id="`${plugin.id}-container`"
      :class="`--${plugin.id}-container`"
    />
    <div v-if="displayPendingKycMessage" :class="`${plugin.id}__pending-kyc`">
      <ds ds="icon" name="pending" size="5rem" color="var(--warning)" class="_m:bottom(5)" />
      <span class="_text:md _m:bottom(3)">
        {{ $t(`component.SectionForm.plugins.${plugin.id}.kycStatus.pending.title`) }}
      </span>
      <span class="_text:sm">
        {{ $t(`component.SectionForm.plugins.${plugin.id}.kycStatus.pending.description`) }}
      </span>
    </div>
  </div>

</template>

<script>

import { LoggerService } from '@/services';
import { eventBus } from '@/eventBus';
import { mapGetters, mapActions } from 'vuex';
import onfidoTheme from '@/assets/themes/onfido/themeConfig.js';
import store from '@/store';
import snsWebSdk from '@sumsub/websdk';

export default {
  name: 'PluginContainer',
  props: {
    plugin: {
      type: Object,
      required: true
    },
    isLocked: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({
    externalSdk: null,
    displayPendingKycMessage: false
  }),
  computed: {
    ...mapGetters(
      {
        kycStatus: 'qualification/kycStatus',
        userEmail: 'qualification/userEmail',
        currentSection: 'qualification/currentSection'
      }
    )
  },
  methods: {
    ...mapActions({
      fetchCurrentSectionSilently: 'qualification/fetchCurrentSectionSilently'
    }),
    async initPluginOnfido (config) {
      if (this.isLocked) return;
      if (!config || !config.data || !config.data.sdkToken) {
        throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
      }
      let { sdkToken, step } = config.data;

      // @todo AG remove this temporary workaround once API always return steps array
      if (!step || !Array.isArray(step) || !step.length) {
        step = [
          'welcome',
          'document',
          'face',
          'complete'
        ];
      }

      const translatedSteps = step.map(s => {
        const key = 'component.SectionForm.plugins.id-check-onfido.steps';
        // different steps require differently strucutred options objects, see: https://documentation.onfido.com/sdk/web/#flow-customization
        if (s === 'welcome' && this.$te(`${key}.${s}`)) {
          return {
            type: s,
            options: {
              title: this.$t(`${key}.${s}.title`),
              descriptions: [
                this.$t(`${key}.${s}.descriptions`)
              ],
              nextButton: this.$t(`${key}.${s}.nextButton`)
            }
          };
        } else if (s === 'complete' && this.$te(`${key}.${s}`)) {
          return {
            type: s,
            options: {
              message: this.$t(`${key}.${s}.message`),
              submessage: this.$t(`${key}.${s}.submessage`)
            }
          };
        }
        return s;
      });

      // init Onfido sdk, see:
      // https://documentation.onfido.com/sdk/web/#no--6-initialize-the-sdk
      try {
        this.externalSdk = window.onfido.init({
          token: sdkToken,
          containerId: `${this.plugin.id}-container`,
          onComplete: () => this.handleKycComplete(),
          onError: () => this.handleKycError(),
          onUserExit: () => this.handleKycUserExit(),
          steps: translatedSteps,
          customUI: onfidoTheme,
          language: store.getters['locale/locale']
        });
      } catch (error) {
        if (error.statusCode === 400) {
          LoggerService.error(
            error.message + '\n\n' +
              (error.errors?.message ? error.errors.message + '\n\n' : '') +
              (error.errors?.data?.response?.error?.message ? error.errors.data.response.error.message + '\n\n' : '') +
              (error.errors?.data?.response?.error?.fields ? errorFields(error.errors.data.response.error.fields, null, 2) : '')
          );
        } else {
          LoggerService.error(error);
        }
      }
    },
    async clearPlugin () {
      if (this.externalSdk?.tearDown) {
        await this.externalSdk.tearDown();
      }
    },
    async handleKycComplete (data) {
      eventBus.$emit('pluginCallback', { pluginId: this.plugin.id, eventId: 'done', ok: true, data });
    },
    async handleKycUserExit (data) {
      eventBus.$emit('pluginCallback', { pluginId: this.plugin.id, eventId: 'cancel', ok: false, data });
    },
    async handleKycError (data) {
      eventBus.$emit('pluginCallback', { pluginId: this.plugin.id, eventId: 'error', ok: false, data });
      await this.externalSdk.safeTearDown();
      switch (this.plugin.id) {
        case 'id-check-onfido':
          this.initPluginOnfido(this.plugin.config);
          break;
        case 'id-check-sumsub':
        {
          this.initPluginSumsub({ ...this.plugin.config, data: { sdkToken: this.plugin.config.data.sdkToken } });
        }
      }
    },
    async initPluginSumsub (config) {
      if (this.isLocked) return;
      if (!config || !config.data || !config.data.sdkToken) {
        throw new Error(this.$t('frontendErrorCodes.UNEXPECTED'));
      }
      const { sdkToken } = config.data;
      try {
        this.externalSdk = snsWebSdk.init(
          sdkToken,
          // token update callback, must return Promise
          // Access token expired
          // get a new one and pass it to the callback to re-initiate the WebSDK
          async () => {
            const section = await this.fetchCurrentSectionSilently(this.currentSection.id);
            return section.plugins[0].config.data.sdkToken;
          }
        )
          .withConf({
            lang: store.getters['locale/locale'], // language of WebSDK texts and comments (ISO 639-1 format)
            uiConf: {
            // customCss: "https://cdnjs.cloudflare.com/ajax/libs/bootstrap/5.2.3/js/bootstrap.min.js"
              customCssStr: ':root {\n  --black: #000000;\n   --grey: #F5F5F5;\n  --grey-darker: #B2B2B2;\n  --border-color: #DBDBDB;\n}\n\np {\n  color: var(--black);\n  font-size: 16px;\n  line-height: 24px;\n}\n\nsection {\n  margin: 40px auto;\n}\n\ninput {\n  color: var(--black);\n  font-weight: 600;\n  outline: none;\n}\n\nsection.content {\n  background-color: var(--grey);\n  color: var(--black);\n  padding: 40px 40px 16px;\n  box-shadow: none;\n  border-radius: 6px;\n}\n\nbutton.submit,\nbutton.back {\n  text-transform: capitalize;\n  border-radius: 6px;\n  height: 48px;\n  padding: 0 30px;\n  font-size: 16px;\n  background-image: none !important;\n  transform: none !important;\n  box-shadow: none !important;\n  transition: all 0.2s linear;\n}\n\nbutton.submit {\n  min-width: 132px;\n  background: none;\n  background-color: var(--black);\n}\n\n.round-icon {\n  background-color: var(--black) !important;\n  background-image: none !important;\n}'
            // URL to css file in case you need change it dynamically from the code
            // the similar setting at Customizations tab will rewrite customCss
            // you may also use to pass string with plain styles `customCssStr:`
            }
          })
          .withOptions({ addViewportTag: false, adaptIframeHeight: true })
          // see below what kind of messages WebSDK generates
          .on('idCheck.onApplicantSubmitted', (payload) => {
            this.handleKycComplete(payload);
          })
          .on('idCheck.onError', (error) => {
            this.handleKycError(error);
          })
          .build();
      } catch (error) {
        LoggerService.error(error);
      }
      // this.externalSdk.launch('#id-check-onfido-container');
      this.externalSdk.launch('#id-check-sumsub-container');
    }
  },

  async mounted () {
    switch (this.plugin.id) {
      case 'id-check-onfido':
        if (this.isLocked && this.kycStatus === 'Pending') {
          this.displayPendingKycMessage = true;
        } else {
          this.initPluginOnfido(this.plugin.config);
        }
        break;
      case 'id-check-sumsub':
        if (this.isLocked && this.kycStatus === 'Pending') {
          this.displayPendingKycMessage = true;
        } else {
          this.initPluginSumsub({ ...this.plugin.config, data: { sdkToken: this.plugin.config.data.sdkToken } });
        }
        break;
      default:
        break;
    }
  },
  async beforeDestroy () {
    await this.clearPlugin();
  }
};

const errorFields = (fields) => {
  const ret = [];
  const recurse = (node, parent = '') => {
    if (Array.isArray(node)) {
      for (const item of node) {
        recurse(item, parent);
      }
    } else if (typeof node === 'object') {
      for (const key of Object.keys(node)) {
        recurse(node[key], parent ? parent + '.' + key : key);
      }
    } else {
      ret.push(parent + ': ' + node);
    }
  };
  recurse(fields);
  return '• ' + ret.join('\n• ');
};
</script>
<style lang="less" scoped>
@import '../../utils.less';

.id-check-onfido__pending-kyc {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 25rem;
  text-align: center;
  padding: calc(var(--gw) * 4) calc(var(--gw) * 5)
}

</style>
